import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { MOCK_SERVER_BASE_URLS } from "src/app/shared/constants/mock-server-base-url.constants";
import { environment } from "src/environments/environment";

type HttpHelperRequestOptions = {
  baseUrl?: string;
  headers?: HttpHeaders;
  withCredentials?: boolean;
};

type HttpHelperPostOptions = HttpHelperRequestOptions & {
  headers?: HttpHeaders;
};

@Injectable({
  providedIn: "root",
})
export class HttpHelperService {
  private URL: string;

  constructor(private readonly http: HttpClient) {
    this.URL = "";
  }

  // Keep the application base URL in one place so every caller can use the
  // same helper without hardcoding environment-specific prefixes.
  public setEnv(url: string): void {
    this.URL = url;
  }

  /**
   * Returns true when the local mock server is active for the current build.
   *
   * Prefer this over reading `environment.mockServerApiEnabled` directly in
   * components and services — it keeps all mock-routing decisions in one place
   * and makes unit tests easier to isolate (spy on the service, not the module).
   */
  public isMockServerActive(): boolean {
    return !!environment.mockServerApiEnabled;
  }

  /**
   * Single source of truth for backend base URL selection.
   *
   * Returns the local mock-server origin when `mockServerApiEnabled` is true
   * so that non-`/common` HTTP helper methods automatically target the correct
   * host without each feature service branching on the environment flag.
   *
   * Requests under `/common` are intentionally excluded and are routed through
   * the dedicated common-service base instead.
   *
   * Falls back to the compiled `apiUrl` when the URL has not yet been set
   * via `setEnv` (e.g. during very early DI construction).
   */
  public resolveBackendBaseUrl(): string {
    if (environment.mockServerApiEnabled) {
      return MOCK_SERVER_BASE_URLS.api;
    }
    return this.URL || environment.apiUrl;
  }

  /**
   * Dedicated base URL for session-switch/navigation endpoints.
   *
   * Header menu redirection now resolves through `sat-motor-svc/v1/session`
   * instead of the legacy `sat-common-svc/api/v1/session` compatibility path.
   */
  public resolveSessionBaseUrl(): string {
    if (environment.mockServerApiEnabled) {
      return MOCK_SERVER_BASE_URLS.session;
    }

    return environment.sessionApiUrl || this.resolveBackendBaseUrl();
  }

  /**
   * Dedicated base URL for auth continuation endpoints hosted on the
   * alpha-sat BFF (first-time login, forgot-password, password-expired).
   */
  public resolveAlphaSatBaseUrl(): string {
    if (environment.mockServerApiEnabled) {
      return MOCK_SERVER_BASE_URLS.api;
    }

    return environment.alphaSatApiUrl || this.resolveBackendBaseUrl();
  }

  private readonly formatErrors = (error: any) => throwError(() => error.error);

  public get(
    path: string,
    params: HttpParams = new HttpParams(),
  ): Observable<any> {
    return this.http
      .get(this.buildUrl(path), { params })
      .pipe(catchError(this.formatErrors));
  }

  public getWithOptions(
    path: string,
    params: HttpParams = new HttpParams(),
    options: HttpHelperRequestOptions = {},
  ): Observable<any> {
    return this.http
      .get(this.buildUrl(path, options.baseUrl), {
        headers: options.headers,
        params,
        withCredentials: !!options.withCredentials,
      })
      .pipe(catchError(this.formatErrors));
  }

  public put(path: string, body: object = {}): Observable<any> {
    return this.http
      .put(`${this.URL}${path}`, JSON.stringify(body))
      .pipe(catchError(this.formatErrors));
  }

  public post(
    path: string,
    body: object = {},
    headerFromRequest?: HttpHeaders,
  ): Observable<any> {
    const headers = headerFromRequest
      ? headerFromRequest
      : new HttpHeaders({ "Content-Type": "application/json" });
    return this.http
      .post(this.buildUrl(path), JSON.stringify(body), {
        headers: headers,
      })
      .pipe(catchError(this.formatErrors));
  }

  public postWithOptions(
    path: string,
    body: object = {},
    options: HttpHelperPostOptions = {},
  ): Observable<any> {
    const headers =
      options.headers ??
      new HttpHeaders({ "Content-Type": "application/json" });
    return this.http
      .post(this.buildUrl(path, options.baseUrl), JSON.stringify(body), {
        headers,
        withCredentials: !!options.withCredentials,
      })
      .pipe(catchError(this.formatErrors));
  }

  public postRaw(
    path: string,
    body: object = {},
    options?: object,
  ): Observable<any> {
    return this.http
      .post(`${this.URL}${path}`, JSON.stringify(body), options)
      .pipe(catchError(this.formatErrors));
  }

  public delete(path: string): Observable<any> {
    return this.http
      .delete(`${this.URL}${path}`)
      .pipe(catchError(this.formatErrors));
  }

  public getURL(path: string) {
    return of(`${this.URL}${path}`);
  }

  public getURLString(path: string): string {
    return `${this.URL}${path}`;
  }

  public getDirectUrl(path: string): Observable<any> {
    return this.http.get(path).pipe(catchError(this.formatErrors));
  }

  public downloadFile(path: string, _body: object = {}): Observable<any> {
    return this.http
      .get(`${this.URL}${path}`, {
        observe: "response",
        responseType: "blob" as "json",
      })
      .pipe(catchError(this.formatErrors));
  }

  public postXmlRequest(path: string) {
    return `${this.URL}${path}`;
  }

  public generateReport(
    path: string,
    body: object = {},
    options?: object,
  ): Observable<any> {
    return this.postRaw(path, body, options);
  }

  private buildUrl(path: string, baseUrl?: string): string {
    if (baseUrl) {
      return `${this.removeTrailingSlash(baseUrl)}${path}`;
    }

    if (this.isCommonPath(path)) {
      return `${this.removeTrailingSlash(
        this.resolveCommonBaseUrl(),
      )}${this.stripCommonPrefix(path)}`;
    }

    return `${this.removeTrailingSlash(this.resolveBackendBaseUrl())}${path}`;
  }

  private resolveCommonBaseUrl(): string {
    if (environment.mockServerApiEnabled) {
      return MOCK_SERVER_BASE_URLS.common;
    }

    return environment.commonApiUrl || this.resolveBackendBaseUrl();
  }

  private isCommonPath(path: string): boolean {
    return path === "/common" || path.startsWith("/common/");
  }

  private stripCommonPrefix(path: string): string {
    if (!this.isCommonPath(path)) {
      return path;
    }

    const suffix = path.slice("/common".length);
    return suffix || "";
  }

  private removeTrailingSlash(url: string): string {
    return url.endsWith("/") ? url.slice(0, -1) : url;
  }
}
